import React, { useState, useMemo, useEffect } from 'react';
import { toArray } from "../utils/toArray";

// Extrait une valeur textuelle d'une cellule qui peut contenir du JSX
const getTextValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(getTextValue).join(' ');
  if (React.isValidElement(value)) {
    if (typeof value.props?.title === 'string') return value.props.title;
    return getTextValue(value.props?.children);
  }
  return '';
};

const Table = ({ columns = [], data = [], globalFilter, onSelectionChange }) => {
  const [sortConfig, setSortConfig] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const rows = toArray(data);
  const cols = toArray(columns);
  const hasSelection = typeof onSelectionChange === 'function';

  // Met à jour le parent chaque fois que la sélection change
  useEffect(() => {
    onSelectionChange?.(Array.from(selectedRows));
  }, [selectedRows, onSelectionChange]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = rows.map(row => row.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };
  
  // ... (la logique de tri et de filtre reste la même) ...
  const filteredData = useMemo(() => {
    if (!globalFilter) return rows;
    const search = globalFilter.toLowerCase();
    return rows.filter(item =>
      cols.some(col =>
        getTextValue(item[col.accessor]).toLowerCase().includes(search)
      )
    );
  }, [rows, cols, globalFilter]);

  const sortedData = useMemo(() => {
    let sortableData = [...filteredData];
    if (sortConfig !== null) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableData;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => { /* ... inchangé ... */ };
  const getSortIndicator = (key) => { /* ... inchangé ... */ };

  return (
    <div className="overflow-x-auto bg-pro-dark-surface rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-pro-dark-border">
          <tr>
            {hasSelection && (
              <th className="p-3 w-4">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={rows.length > 0 && selectedRows.size === rows.length}
                  className="form-checkbox h-4 w-4 bg-pro-dark-surface border-pro-dark-border rounded text-pro-dark-accent focus:ring-pro-dark-accent"
                />
              </th>
            )}
            {cols.map((col) => (
              <th
                key={col.accessor}
                onClick={() => requestSort(col.accessor)}
                className="p-3 text-left whitespace-nowrap cursor-pointer hover:bg-gray-700"
              >
                {col.Header}{getSortIndicator(col.accessor)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <tr
              key={row.id}
              className={`border-b border-pro-dark-border last:border-0 ${selectedRows.has(row.id) ? 'bg-pro-dark-accent/10' : ''}`}
            >
              {hasSelection && (
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.id)}
                    onChange={() => handleSelectRow(row.id)}
                    className="form-checkbox h-4 w-4 bg-pro-dark-surface border-pro-dark-border rounded text-pro-dark-accent focus:ring-pro-dark-accent"
                  />
                </td>
              )}
              {cols.map((col) => (
                <td key={col.accessor} className="p-3 whitespace-nowrap">
                  {row[col.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;